/* eslint-disable @angular-eslint/no-output-on-prefix */
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { SelectionEventType } from "@components/tree-selector/SelectionEventType";
import { TreeSelectorComponent as TreeSelectorComponent_1 , TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { SelectionEvent } from "~app/common/components/tree-selector/SelectionEvent";

import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IProduktionsmall } from "../../arbetsorder-produktval/arbetsorder-produktval.models";
import { AddArbetsorderHandelssortimentCommand } from "../../commands/AddArbetsorderHandelssortimentCommand";
import { DeleteArbetsorderHandelssortimentCommand } from "../../commands/DeleteArbetsorderHandelssortimentCommand";
import { SetArbetsorderHandelssortimentAngivenKvantitetCommand } from "../../commands/SetArbetsorderHandelssortimentAngivenKvantitetCommand";
import { IArbetsorderHandelssortiment } from "../../model/IArbetsorderHandelssortiment";
import { IArbetsorderHandelssortimentsgrupp } from "../../model/IArbetsorderHandelssortimentsgrupp";
import { ArbetsorderDisabledFlag, ArbetsorderWrapper } from "../../planera-detail/planera-detail-models";
import { HandelssortimentSelectionModel } from "../models/HandeslssortimentSelectionModel";

@Component({
    selector: "rask-arbetsorder-handelssortimentsval-rad",
    templateUrl: "./arbetsorder-handelssortimentsval-rad.component.html",
    styleUrls: ["./arbetsorder-handelssortimentsval-rad.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        ActionButtonComponent,
        TreeSelectorComponent_1,
        InputNumberComponent,
        FormsModule,
        NgbCollapse
    ]
})
export class ArbetsorderHandelssortimentsvalRadComponent implements OnInit, OnChanges {

    @Input() handelssortimentsgrupp: IArbetsorderHandelssortimentsgrupp;
    @Input() arbetsorderWrapper: ArbetsorderWrapper;
    @Input() produktionsmall: IProduktionsmall;
    @ViewChild("tree") treeSelector: TreeSelectorComponent;
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output("onSelectionEvent") selectionEvent = new EventEmitter<any>();

    handelssortimentSelectionItems: HandelssortimentSelectionModel[];

    get hasRows(): boolean {
        return this.handelssortimentSelectionItems?.some(x => x.selected);
    }

    get selectedRows(): HandelssortimentSelectionModel[] {
        return this.handelssortimentSelectionItems?.filter(x => x.selected);
    }

    private readonly authenticationService = inject(AuthenticationService);

    ngOnChanges(changes: SimpleChanges): void {
        if(!ObjectUtils.isNullOrUndefined(changes.produktionsmall.currentValue)) {
            this.unSelectValues();
            this.handelssortimentSelectionItems?.forEach(x => {
                if(this.produktionsmall.handelssortiment.some(y => y.handelssortimentId === x.value)) {
                    x.selected = true;
                    this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(
                        new AddArbetsorderHandelssortimentCommand(
                            x.value,
                            this.arbetsorderWrapper.arbetsorder.id
                        ));
                }
            });
        }
    }

    canEdit = () => this.authenticationService.canEdit() &&
        this.arbetsorderWrapper?.arbetsorder?.isEditableForMode(ArbetsorderDisabledFlag.makulerad) &&
        !this.arbetsorderWrapper.arbetsorder.isExternArbetsorder;

    treeSelectorSortExpression = (a, b) => (a.text.localeCompare(b.text) ? 1 : -1);

    ngOnInit(): void {
        this.handelssortimentSelectionItems = this.handelssortimentsgrupp.handelssortiment.map(x => ({
            hasProduktionsmal: x.hasProduktionsmal,
            text: x.namn,
            id: x.handelssortimentId,
            value: x.handelssortimentId,
            selected: this.arbetsorderWrapper.arbetsorder.handelssortiment.some(y => y.handelssortimentId === x.handelssortimentId),
            kvantitet: this.arbetsorderWrapper.arbetsorder.handelssortiment.find(y => y.handelssortimentId === x.handelssortimentId)?.angivenKvantitet
        } as HandelssortimentSelectionModel));
    }

    onTreeSelectionEvent($event: SelectionEvent) {
        if ($event.type === SelectionEventType.Checked) {
            this.arbetsorderWrapper.arbetsorder.handelssortiment.push(
                {
                    handelssortimentId: $event.id
                } as IArbetsorderHandelssortiment);
            this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(
                new AddArbetsorderHandelssortimentCommand(
                    $event.id,
                    this.arbetsorderWrapper.arbetsorder.id
                ));
        } else {
            this.removeHandelssortimentFromArbetsorder($event.id);
            this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(
                new DeleteArbetsorderHandelssortimentCommand(
                    $event.id,
                    this.arbetsorderWrapper.arbetsorder.id
                ));
        }
        this.selectionEvent.emit($event);
    }

    showTree($event) {
        this.treeSelector.toggle();
    }

    onAngivenKvantitetChange($event: any, rad: HandelssortimentSelectionModel) {
        this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(
            new SetArbetsorderHandelssortimentAngivenKvantitetCommand(rad.value, this.arbetsorderWrapper.arbetsorder.id, rad.kvantitet)
        );
    }

    private unSelectValues() {
        this.handelssortimentSelectionItems?.forEach(x => {
            if (x.selected) {
                x.selected = false;
                this.removeHandelssortimentFromArbetsorder(x.value);
                this.arbetsorderWrapper.arbetsorder.commandManager.updateCommand(
                    new DeleteArbetsorderHandelssortimentCommand(
                        x.value,
                        this.arbetsorderWrapper.arbetsorder.id
                    ));
            }
        });
    }

    private removeHandelssortimentFromArbetsorder(handelssortimentId: number) {
        const index = this.arbetsorderWrapper.arbetsorder.handelssortiment.indexOf(
            this.arbetsorderWrapper.arbetsorder.handelssortiment.find(y => y.handelssortimentId === handelssortimentId), 0);
        if (index > -1) {
            this.arbetsorderWrapper.arbetsorder.handelssortiment.splice(index, 1);
        }
    }
}
